// @ts-strict-ignore
import * as React from 'react';
import Tooltip from 'antd/lib/tooltip';
import {
    AutomationType,
    DisabledAutomation,
    DisabledIntegration,
    DisabledOrLimitedSubscriptionFeature,
    IntegrationCapability,
    SubscriptionFeatureType,
    SubscriptionFeaturesAndCapabilities,
    SubscriptionStatus,
} from '../../types';
import { runwayColors, antGrey } from '../styles/globalStyles';
import { useSelector } from 'react-redux';
import {
    selectCurrentFeaturesAndCapabilities,
    selectFeaturesAndCapabilitiesPerSubscriptionStatus,
    selectOrg,
    selectCurrentSubscriptionStatus,
    selectIsShowingTrialBanner,
    selectIsTrialEnded,
    selectIsTrialStarted,
    selectTrialType,
    selectIsDemoInstanceOrDemoModeEnabled,
    selectShouldDisableFeatureIntegrationCapabilityOrAutomationForSubscriptionType,
} from '../../selectors/selectors';
import { RootState } from '../../reducers/reducers';
import {
    getTooltipContentForFeatureOrCapability,
    tooltipContentForFeatureOrCapabilityUsingTrialType,
    useRestrictedFeatureIsDisabled,
} from './planRestrictionHelperComponents';
import SubscriptionPlanIcon from './SubscriptionPlanIcon';

export interface PlanRestrictedFeatureWrapperProps {
    feature?: SubscriptionFeatureType;
    integrationCapability?: IntegrationCapability;
    automationType?: AutomationType;
    requiresTooltip?: boolean;
    tooltipNoIcon?: boolean;
    shouldFakeDisabledState?: boolean;
    shouldGrayOutOnFakeDisabledState?: boolean;
    children?: React.ReactNode | ((isDisabled: boolean, limit?: number) => React.ReactNode);
    mini?: boolean;
}

const getFeatureOrCapabilityData = (params: {
    feature?: SubscriptionFeatureType;
    automationType?: AutomationType;
    integrationCapability?: IntegrationCapability;
    currentFeaturesAndCapabilities: SubscriptionFeaturesAndCapabilities;
}): DisabledOrLimitedSubscriptionFeature | DisabledIntegration | DisabledAutomation => {
    const { feature, automationType, integrationCapability, currentFeaturesAndCapabilities } = params;

    if (!currentFeaturesAndCapabilities) {
        return null;
    }

    if (feature) {
        return currentFeaturesAndCapabilities.disabledOrLimitedFeaturesMap[feature];
    }

    if (automationType) {
        return currentFeaturesAndCapabilities.disabledAutomationsMap[automationType];
    }

    if (integrationCapability) {
        return currentFeaturesAndCapabilities.disabledIntegrationCapabilitiesMap[integrationCapability];
    }

    return null;
};

export const PlanRestrictedFeatureWrapper: React.FC<PlanRestrictedFeatureWrapperProps> = ({
    feature,
    integrationCapability,
    automationType,
    children = null,
    shouldFakeDisabledState,
    shouldGrayOutOnFakeDisabledState = true,
    requiresTooltip,
    tooltipNoIcon,
    mini,
    ...rest
}) => {
    const orgId = useSelector((state: RootState) => selectOrg(state).id);
    const isShowingTrialBanner = useSelector((state: RootState) => selectIsShowingTrialBanner(state));
    const isTrialStarted = useSelector((state: RootState) => selectIsTrialStarted(state));
    const isTrialEnded = useSelector((state: RootState) => selectIsTrialEnded(state));
    const trialType = useSelector((state: RootState) => selectTrialType(state));
    const subscriptionStatus = useSelector((state: RootState) => selectCurrentSubscriptionStatus(state));
    const isDemoInstanceOrDemoMode = useSelector((state: RootState) =>
        selectIsDemoInstanceOrDemoModeEnabled(state, orgId),
    );

    const currentFeaturesAndCapabilities = useSelector((state: RootState) =>
        selectCurrentFeaturesAndCapabilities(state),
    );

    const currentSubscriptionStatus = useSelector((state: RootState) => selectCurrentSubscriptionStatus(state));

    const featuresAndCapabilitiesPerSubscriptionStatus = useSelector((state: RootState) =>
        selectFeaturesAndCapabilitiesPerSubscriptionStatus(state),
    );

    const isFeatureEnabled = !useRestrictedFeatureIsDisabled(feature, automationType, integrationCapability);

    const currentFeatureOrCapabilityDisabledData = getFeatureOrCapabilityData({
        feature,
        automationType,
        integrationCapability,
        currentFeaturesAndCapabilities,
    });

    const normallyUsedFeatureOrCapabilityDisabledData = getFeatureOrCapabilityData({
        feature,
        automationType,
        integrationCapability,
        currentFeaturesAndCapabilities: featuresAndCapabilitiesPerSubscriptionStatus[currentSubscriptionStatus],
    });

    const disabledDataForFeatureCapabilityOrAutomationOnSubscriptionFree = getFeatureOrCapabilityData({
        feature,
        automationType,
        integrationCapability,
        currentFeaturesAndCapabilities: featuresAndCapabilitiesPerSubscriptionStatus[SubscriptionStatus.Free],
    });

    const wouldFeatureBeNormallyDisabled = useSelector((state: RootState) => {
        return selectShouldDisableFeatureIntegrationCapabilityOrAutomationForSubscriptionType(
            state,
            currentSubscriptionStatus,
            feature,
            automationType,
            integrationCapability,
        );
    });

    const isFeatureDisabledForSubscriptionFree = useSelector((state: RootState) => {
        return selectShouldDisableFeatureIntegrationCapabilityOrAutomationForSubscriptionType(
            state,
            SubscriptionStatus.Free,
            feature,
            automationType,
            integrationCapability,
        );
    });

    const wouldFeatureBeNormallyEnabled = !wouldFeatureBeNormallyDisabled;
    const isFeatureDisabled = isDemoInstanceOrDemoMode ? false : !isFeatureEnabled;

    const processedChildren =
        typeof children === 'function' ?
            children(
                isFeatureDisabled,
                (currentFeatureOrCapabilityDisabledData as DisabledOrLimitedSubscriptionFeature)?.limit,
            )
        :   children;

    const propsInjectedChildren =
        (processedChildren as React.ReactElement)?.type === undefined ?
            processedChildren
        :   React.cloneElement(processedChildren, { ...rest });

    let content = <>{propsInjectedChildren}</>;

    // demo mode always shows tooltips for any feature thats restricted to plans above free
    if (isDemoInstanceOrDemoMode) {
        const tooltipContentForFeature = getTooltipContentForFeatureOrCapability({
            feature,
            automationType,
            integrationCapability,
            featuresAndCapabilitiesPerSubscriptionStatus,
            featureCapabilityOrAutomationDisabledData: disabledDataForFeatureCapabilityOrAutomationOnSubscriptionFree,
            isTrialActive: false,
        });

        if (isFeatureDisabledForSubscriptionFree && requiresTooltip && tooltipContentForFeature) {
            return contentWithIconTooltipWithContent(tooltipContentForFeature, content, tooltipNoIcon);
        }

        return content;
    }

    // if the feature is enabled and it normally is, no message needs to be rendered
    if (isFeatureEnabled && wouldFeatureBeNormallyEnabled) {
        return content;
    }

    // if the feature is enabled, and it normally shouldn't be, but they're not on a trial, then its an override and we don't need a message
    if (isFeatureEnabled && !wouldFeatureBeNormallyEnabled && !isShowingTrialBanner) {
        return content;
    }

    if (!isFeatureEnabled && shouldFakeDisabledState) {
        content = (
            <div
                style={{
                    display: 'block',
                    opacity: 0.5,
                    filter: shouldGrayOutOnFakeDisabledState ? 'grayscale(100%)' : 'none',
                    pointerEvents: 'none',
                }}
            >
                {content}
            </div>
        );
    }

    const tooltipContentForTrialType = tooltipContentForFeatureOrCapabilityUsingTrialType({
        orgId: orgId,
        feature,
        automationType,
        integrationCapability,
        featureCapabilityOrAutomationDisabledData: normallyUsedFeatureOrCapabilityDisabledData,
        isTrialStarted: isTrialStarted,
        isTrialEnded: isTrialEnded,
        trialType: trialType,
        subscriptionStatus,
        featuresAndCapabilitiesPerSubscriptionStatus,
    });

    const shouldShowTooltip = wouldFeatureBeNormallyDisabled && requiresTooltip && tooltipContentForTrialType;
    if (!shouldShowTooltip) {
        return content;
    }

    return contentWithIconTooltipWithContent(tooltipContentForTrialType, content, tooltipNoIcon, mini);
};

const contentWithIconTooltipWithContent: (
    tooltipContent: React.ReactNode,
    content: React.ReactNode,
    hideIcon: boolean,
    mini?: boolean,
) => React.ReactElement = (tooltipContent, content, hideIcon, mini = false) => {
    if (hideIcon) {
        return (
            <Tooltip
                title={tooltipContent}
                placement="topLeft"
                overlayInnerStyle={{
                    color: antGrey.grey9,
                }}
                color={runwayColors.white}
                align={{
                    offset: [-12, 0],
                }}
            >
                <div>{content}</div>
            </Tooltip>
        );
    } else {
        return (
            <div
                style={{
                    display: 'flex',
                    gap: 16,
                    alignItems: 'center',
                    minWidth: 0,
                }}
            >
                <Tooltip
                    title={tooltipContent}
                    placement="topLeft"
                    overlayInnerStyle={{
                        color: antGrey.grey9,
                    }}
                    color={runwayColors.white}
                    align={{
                        offset: [-12, 0],
                    }}
                >
                    <div>
                        <SubscriptionPlanIcon mini={mini} />
                    </div>
                </Tooltip>
                {content}
            </div>
        );
    }
};
